import { spacing } from "@certn/cascadia-tokens";
import { formatFileSize, truncatedFileName } from "@certn/utils";
import { css } from "@emotion/css";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ActionList,
  ActionListItem,
  FormFeedback,
  Link,
  List,
  Loader,
  Text,
} from "@certn/cascadia-react";

import { FileSubtitle } from "./FileSubtitle";
import { Upload, UploadProps } from "./Upload";
import { UploadError } from "./UploadError";
import { FileInfo } from "./utils";
import { FormLabel } from "../../components/inputs/_assets/input-wrapper/FormLabel";
import { Icon } from "../../foundations/icons/Icon/Icon";

export interface FileUploadProps extends Omit<UploadProps, "onFileSelected"> {
  label?: string;
  info?: React.ReactNode;
  error?: React.ReactNode;
  setErrors?(errors: string[]): void;
  showFileInfo?: "size" | "type";
  files?: FileInfo[];
  onSelected(file: File): Promise<void>;
  onRemove?: (file: FileInfo) => void;
  testID?: string;
  isRequired?: boolean;
}

/**
 * @deprecated use FileUploadRAC instead
 */
export function FileUpload(props: FileUploadProps) {
  const { t } = useTranslation();

  const {
    label,
    error,
    showFileInfo,
    files = [],
    onSelected,
    testID = "file-upload",
    ...uploadProps
  } = props;

  const [errors, setErrors] = useState<string[]>([]);
  const [uploading, setUploading] = useState<File[]>([]);
  const cancelFiles = useRef<FileInfo[]>([]);
  const handleFilesSelected = async (files: FileList) => {
    setErrors([]);

    setUploading((currFiles) => [...currFiles, ...files]);

    for (const file of files) {
      try {
        if (!file.type) {
          throw new UploadError("unsupported");
        }

        if (props.fileTypes && !props.fileTypes.includes(file.type)) {
          throw new UploadError("unsupported");
        }

        if (props.maxSizeBytes && file.size > props.maxSizeBytes) {
          throw new UploadError("too-big");
        }

        if (cancelFiles.current.includes(file)) {
          cancelFiles.current = cancelFiles.current.filter(
            (currFile) => currFile !== file
          );
          continue;
        }

        await onSelected(file);
      } catch (e) {
        if (e instanceof UploadError && e.code === "unsupported") {
          setErrors((currErrors) => [
            ...currErrors,
            t("{{filename}} because this file type is not supported.", {
              filename: file.name,
            }),
          ]);
          continue;
        }

        if (e instanceof UploadError && e.message === "too-big") {
          setErrors((currErrors) => [
            ...currErrors,
            t("{{filename}} because the file size exceeds {{filesize}}.", {
              filename: file.name,
              filesize: formatFileSize(props.maxSizeBytes || 0),
            }),
          ]);
          continue;
        }

        setErrors((currErrors) => [
          ...currErrors,
          t("{{filename}} because something went wrong.", {
            filename: file.name,
          }),
        ]);
      } finally {
        setUploading((currUploading) =>
          currUploading.filter((currFile) => currFile !== file)
        );
      }
    }
  };

  const showFileInput =
    props.multiple || (files.length < 1 && uploading.length < 1);

  return (
    <div
      className={css(styles.wrapper, !showFileInput && styles.noGap)}
      data-testid={testID}
    >
      <div>
        {label && (
          <FormLabel
            testID={`${testID}-label`}
            isRequired={props.isRequired}
            info={props.info}
          >
            {label}
          </FormLabel>
        )}
        {showFileInput && (
          <>
            <Upload
              testID={`${testID}-upload`}
              onFileSelected={handleFilesSelected}
              {...uploadProps}
            />
            {error && <FormFeedback type="invalid">{error}</FormFeedback>}
            {errors.length === 1 && (
              <FormFeedback type="invalid">
                {t("Unable to upload {{error}}", { error: errors[0] })}
              </FormFeedback>
            )}
            {errors.length > 1 && (
              <FormFeedback type="invalid">
                {t("The following files failed to upload:")}
                <List type="unordered">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </List>
              </FormFeedback>
            )}
          </>
        )}
      </div>

      <div className={styles.fileList} data-testid={`${testID}-file-list`}>
        {files.map((file, index) => {
          const [start, end] = truncatedFileName(file.name);
          return (
            <ActionList key={index}>
              <ActionListItem
                alignItems="center"
                leading={
                  file.type.includes("image") ? (
                    <img src={file.url || file.content} alt={file.name} />
                  ) : (
                    <Icon
                      name="description"
                      color="background-accent-blue-strong"
                      size="space-6"
                    />
                  )
                }
                trailing={
                  props.onRemove ? (
                    <Link
                      linkStyle="monochrome"
                      testID="remove-file"
                      textColor="text-weak"
                      textStyle="global/body-secondary"
                      onPress={() => {
                        props.onRemove && props.onRemove(file);
                      }}
                    >
                      {t("Remove")}
                    </Link>
                  ) : undefined
                }
                title={
                  <>
                    <Text truncate>{start}</Text>
                    <Text>{end}</Text>
                  </>
                }
              >
                <FileSubtitle fileInfo={showFileInfo} file={file} />
              </ActionListItem>
            </ActionList>
          );
        })}

        {uploading.map((file, index) => {
          return (
            <ActionList key={index}>
              <ActionListItem
                leading={<Loader type="spinner" size="small" />}
                trailing={
                  <Link
                    testID="cancel-upload"
                    textColor="text-weak"
                    textStyle="global/body-secondary"
                    onPress={() => {
                      cancelFiles.current.push(file);
                      setUploading((currFiles) =>
                        currFiles.filter((currFile) => currFile !== file)
                      );
                    }}
                  >
                    {t("Cancel")}
                  </Link>
                }
                title={file.name}
              >
                <FileSubtitle fileInfo={showFileInfo} file={file} />
              </ActionListItem>
            </ActionList>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: spacing["space-3"].px,
  }),
  noGap: css({
    gap: 0,
  }),
  fileList: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
};
